import React, { useEffect } from "react";
import "uikit/fonts/montserrat.css";
import { Route } from "react-router";
import { Redirect, Switch, useLocation } from "react-router-dom";
import { useContextPerson } from "hooks/persons";
import { Loader } from "uikit/components";
import Layout from "app/layout/Layout/Layout";
import HomePage from "app/home/HomePage/HomePage";
import NoAccess from "app/home/NoAccess/NoAccess";
import SportsmanPage from "app/dashboard/SportsmanPage/SportsmanPage";
import { MediaContextProvider } from "./media/MediaContext";
import RegionSummaryPage from "app/dashboard/RegionReportPage/RegionSummaryPage";
import SchoolSummaryPage from "app/dashboard/SchoolSummaryPage/SchoolSummaryPage";
import { useMetrics } from "metrics/MetricsContext";
import { useAppVersionMismatchNotification } from "hooks/useAppVersionMismatchNotification";
import { useLogHistoryChanges } from "hooks/useLogHistoryChanges";
import RegionSchoolsAttendancePage from "app/dashboard/RegionSchoolsAttendancePage/RegionSchoolsAttendancePage";
import SchoolGroupsAttendancePage from "app/dashboard/SchoolGroupsAttendancePage/SchoolGroupsAttendancePage";
import RegionEventsPage from "app/dashboard/RegionEventsPage/RegionEventsPage";
import FederationEventsPage from "app/dashboard/FederationEventsPage/FederationEventsPage";
import BuildingLoadingPage from "app/dashboard/BuildingLoadingPage/BuildingLoadingPage";
import MunicipalityLoadingPage from "app/dashboard/MunicipalityLoadingPage/MunicipalityLoadingPage";
import RegionLoadingPage from "app/dashboard/RegionLoadingPage/RegionLoadingPage";
import FederationSummaryPage from "app/dashboard/FederationReportPage/FederationSummaryPage";
import FederationLoadingPage from "app/dashboard/FederationLoadingPage/FederationLoadingPage";
import MunicipalitySummaryPage from "app/dashboard/MunicipalitySummaryPage/MunicipalitySummaryPage";
import MunicipalitySchoolsAttendancePage from "app/dashboard/MunicipalitySchoolsAttendancePage/MunicipalitySchoolsAttendancePage";
import { HelmetProvider } from "react-helmet-async";
import SchoolTrainingPlansPage from "app/dashboard/SchoolTrainingPlansPage/SchoolTrainingPlansPage";
import CoachStatisticsPage from "app/dashboard/CoachStatisticsPage/CoachStatisticsPage";
import RegionTrainingPlansPage from "app/dashboard/RegionTrainingPlansPage/RegionTrainingPlansPage";
import MunicipalityTrainingPlansPage from "app/dashboard/MunicipalityTrainingPlansPage/MunicipalityTrainingPlansPage";
import { SsoLoginError, SsoLoginRedirect } from "app/home/Sso";
import OrganizationSelectPage from "app/home/OrganizationSelectPage/OrganizationSelectPage";

const DashboardRoutes = ({
    isSuperUser,
    suggestedRegionId,
    suggestedMunicipalityId,
    suggestedSchoolId,
    suggestedFederationId,
}) => (
    <Switch>
        {isSuperUser && <Route exact path="/" component={HomePage} />}
        {isSuperUser && <Route exact path="/sportsman/:sportsmanId" component={SportsmanPage} />}
        <Route exact path="/region-summary/:regionId" component={RegionSummaryPage} />
        <Route exact path="/attendance/region/:regionId" component={RegionSchoolsAttendancePage} />
        <Route
            exact
            path="/attendance/municipality/:municipalityId"
            component={MunicipalitySchoolsAttendancePage}
        />
        <Route
            exact
            path="/attendance/region/:regionId/school/:schoolId"
            component={SchoolGroupsAttendancePage}
        />
        <Route
            exact
            path="/attendance/municipality/:municipalityId/school/:schoolId"
            component={SchoolGroupsAttendancePage}
        />
        <Route exact path="/attendance/school/:schoolId" component={SchoolGroupsAttendancePage} />
        <Route exact path="/federation-events/region/:regionId" component={RegionEventsPage} />
        <Route
            exact
            path="/federation-events/federation/:federationId"
            component={FederationEventsPage}
        />
        <Route exact path="/federation-summary/:federationId" component={FederationSummaryPage} />
        <Route
            exact
            path="/municipality-summary/:municipalityId"
            component={MunicipalitySummaryPage}
        />
        <Route exact path="/school-summary/:schoolId" component={SchoolSummaryPage} />
        <Route exact path="/training-plans/school/:schoolId" component={SchoolTrainingPlansPage} />
        <Route
            exact
            path="/training-plans/region/:regionId/school/:schoolId"
            component={SchoolTrainingPlansPage}
        />
        <Route
            exact
            path="/training-plans/municipality/:municipalityId/school/:schoolId"
            component={SchoolTrainingPlansPage}
        />
        <Route exact path="/training-plans/region/:regionId" component={RegionTrainingPlansPage} />
        <Route
            exact
            path="/training-plans/municipality/:municipalityId"
            component={MunicipalityTrainingPlansPage}
        />
        <Route exact path="/coach-statistics/school/:schoolId" component={CoachStatisticsPage} />
        <Route exact path="/loading/region/:regionId" component={RegionLoadingPage} />
        <Route
            exact
            path="/loading/region/:regionId/municipality/:municipalityId"
            component={MunicipalityLoadingPage}
        />
        <Route
            exact
            path="/loading/region/:regionId/municipality/:municipalityId/building/:buildingId"
            component={BuildingLoadingPage}
        />
        <Route exact path="/loading/federation/:federationId" component={FederationLoadingPage} />
        {/* Не нашли запрошенной страницы, доступной пользователю. Пробуем отправить его на какую-то вменяемую страницу. */}
        {isSuperUser && <Redirect to="/" />}
        {/* Суперюзер может состоять в школе и/или ОИВе, но мы просто отправляем его на главную. */}
        {suggestedRegionId && <Redirect to={`/region-summary/${suggestedRegionId}`} />}
        {/* Мы знаем регион под контролем пользователя, отправляем его туда. */}
        {suggestedMunicipalityId && (
            <Redirect to={`/municipality-summary/${suggestedMunicipalityId}`} />
        )}
        {/* Мы знаем муниципалитет под контролем пользователя, отправляем его туда. */}
        {suggestedSchoolId && <Redirect to={`/school-summary/${suggestedSchoolId}`} />}
        {/* Мы знаем школу пользователя, отправляем в нее. */}
        {suggestedFederationId && <Redirect to={`/federation-summary/${suggestedFederationId}`} />}
        {/* Мы знаем одну из федераций пользователя, отправляем в нее. */}
        {/* Если пользователь не суперюзер - у него пока нет "главной" страницы на корне ("/"). Если мы дошли до этого роута - выше нам было некуда отправить пользователя. Просто показываем на корне пустую страницу. */}
        <Route exact path="/">
            <NoAccess />
        </Route>
        {/* Пользователь непонятно где и мы не можем отправить его на какую-то адекватную страницу, раз мы оказались тут. Отправляем на корень. */}
        <Redirect to="/" />
    </Switch>
);

const LayoutRoutes = ({ person }) => {
    if (!person.hasAccessToDashboard) {
        return <NoAccess />;
    }

    return (
        <DashboardRoutes
            isSuperUser={person.isSuperUser}
            suggestedRegionId={person.suggestedRegionId}
            suggestedMunicipalityId={person.suggestedMunicipalityId}
            suggestedSchoolId={person.suggestedSchoolId}
            suggestedFederationId={person.suggestedFederationId}
        />
    );
};

function usePageViews() {
    const { openPageEvent } = useMetrics();
    const location = useLocation();
    useEffect(() => {
        openPageEvent();
    }, [location, openPageEvent]);
}

function App() {
    usePageViews();
    useAppVersionMismatchNotification();
    useLogHistoryChanges();

    const { isLoading, isAuthenticated, person, isPersonAuthenticated } = useContextPerson();
    const location = useLocation();

    if (isLoading) {
        return <Loader position="margin-auto" />;
    }

    const showSsoLoginError = location.pathname === "/public/sso-login-error";
    if (!showSsoLoginError && !isAuthenticated) {
        return <SsoLoginRedirect />;
    }

    const helmetContext = {};
    return (
        <MediaContextProvider>
            <HelmetProvider context={helmetContext}>
                <Layout isAuthenticated={isAuthenticated}>
                    {showSsoLoginError ? (
                        <SsoLoginError />
                    ) : (
                        <Switch>
                            <Route exact path="/organizations" component={OrganizationSelectPage} />
                            <ForcePersonAuthenticated isPersonAuthenticated={isPersonAuthenticated}>
                                <LayoutRoutes person={person} />
                            </ForcePersonAuthenticated>
                        </Switch>
                    )}
                </Layout>
            </HelmetProvider>
        </MediaContextProvider>
    );
}

function ForcePersonAuthenticated({ isPersonAuthenticated, children }) {
    const location = useLocation();

    if (!isPersonAuthenticated) {
        return (
            <Redirect
                to={{
                    pathname: "/organizations",
                    state: {
                        referrer: location.pathname,
                    },
                }}
            />
        );
    }

    return <>{children}</>;
}

export default App;
